/**
 * Error reporting service — thin abstraction over Sentry.
 *
 * Stub mode (no VITE_SENTRY_DSN): Sentry is never initialized and
 * `captureException` writes the last error to sessionStorage for debugging.
 *
 * Active mode (VITE_SENTRY_DSN set at build time): errors, performance
 * tracing (page loads, route navigations, API calls) and error-only session
 * replay are sent to Sentry.
 *
 * One DSN is used for every deployment. The Sentry `environment` is resolved
 * at runtime from the hostname — the same mechanism `buildApiBase` uses to
 * pick API hosts — because a single version-tagged image can be promoted
 * across environments:
 *
 *   localhost / Vite dev server            → local
 *   *.sgpdev.com                           → dev
 *   spiglassstage.safeguardproperties.com  → stage   (any "stage"/"staging" label)
 *   *.safeguardproperties.com              → prod
 *   anything else                          → unknown
 *
 * Set VITE_SENTRY_ENVIRONMENT to force a value (e.g. for a new host).
 *
 * All call sites (ErrorBoundary, axiosClient error interceptor, routes) call
 * these functions, so no further wiring is needed per feature.
 */

import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { getBaseDomain } from '@/lib/authHost';
import { scrubQueryString, scrubUrl, scrubUrlsDeep, scrubUrlsInText } from '@/lib/scrubUrl';

export type SentryEnvironment = 'local' | 'dev' | 'stage' | 'prod' | 'unknown';

const SENTRY_DSN =
  typeof import.meta !== 'undefined' ? (import.meta.env?.VITE_SENTRY_DSN ?? '') : '';
const IS_ENABLED = Boolean(SENTRY_DSN);

const DEV_ROOT_DOMAIN = 'sgpdev.com';
const PROD_ROOT_DOMAIN = 'safeguardproperties.com';
const STAGE_LABEL = /stag(e|ing)/i;

/** Fraction of page loads / navigations traced, per environment. */
const TRACES_SAMPLE_RATE: Record<SentryEnvironment, number> = {
  local: 1.0,
  dev: 1.0,
  stage: 0.5,
  prod: 0.1,
  unknown: 0.1,
};

/**
 * Fraction of errored sessions that upload a replay, per environment. Prod is
 * sampled so a backend outage (every request 500s) cannot exhaust the replay
 * quota; lower environments keep every replay for debugging.
 */
const REPLAY_ON_ERROR_SAMPLE_RATE: Record<SentryEnvironment, number> = {
  local: 1.0,
  dev: 1.0,
  stage: 1.0,
  prod: 0.25,
  unknown: 0.25,
};

/**
 * Console messages (first argument) whose logged values must not reach
 * Sentry. They still print to the browser console; Sentry's console
 * breadcrumb (also shown in replays) keeps only the message text.
 */
const CONSOLE_MESSAGES_WITHOUT_DETAILS = new Set([
  // Logs the full /vendor/v1/me response (vendorName, createBy, updateBy).
  '[VendorAuthGuard] /vendor/v1/me returned no vendorCode:',
]);

/** Drop logged values from listed console breadcrumbs. Exported for unit tests. */
export function redactConsoleBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
  const args: unknown = breadcrumb.data?.arguments;
  if (breadcrumb.category !== 'console' || !Array.isArray(args)) return;
  const [first] = args;
  if (typeof first === 'string' && CONSOLE_MESSAGES_WITHOUT_DETAILS.has(first)) {
    breadcrumb.message = first;
    breadcrumb.data = { ...breadcrumb.data, arguments: [first] };
  }
}

/**
 * Map a hostname to a Sentry environment. Pure — exported for unit tests.
 */
export function resolveSentryEnvironment(
  hostname: string = window.location.hostname,
  override: string | undefined = import.meta.env?.VITE_SENTRY_ENVIRONMENT
): SentryEnvironment | string {
  if (override) return override;
  if (import.meta.env?.DEV) return 'local';

  const host = hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') return 'local';

  const base = getBaseDomain(host);
  if (base === DEV_ROOT_DOMAIN) return 'dev';
  if (base === PROD_ROOT_DOMAIN) {
    const subdomain = host.slice(0, -(base.length + 1));
    return STAGE_LABEL.test(subdomain) ? 'stage' : 'prod';
  }
  return 'unknown';
}

export function initErrorReporting(): void {
  if (!IS_ENABLED) {
    return;
  }

  const environment = resolveSentryEnvironment();
  const release = import.meta.env.VITE_SENTRY_RELEASE;

  const replay = Sentry.replayIntegration({
    maskAllText: true,
    maskAllInputs: true,
    blockAllMedia: true,
    // blockAllMedia does not cover iframes; their src (signed MindStudio
    // URLs, SafeView/CoreScript keys and order ids) would otherwise be
    // recorded. Cross-origin iframe content is never recorded anyway.
    block: ['iframe'],
    maskAttributes: ['title', 'placeholder', 'aria-label', 'href', 'src'],
    // Scrub URLs in navigation/breadcrumb frames. The SDK only passes custom
    // events here — the page URL stored with each full snapshot is covered
    // by pauseReplayOnSensitiveUrl below instead.
    beforeAddRecordingEvent(event) {
      scrubUrlsDeep(event);
      return event;
    },
  });

  Sentry.init({
    dsn: SENTRY_DSN,
    environment,
    release: release ? `spiglass-react@${release}` : undefined,

    // This app handles client/property data and authenticates with a Bearer
    // cookie — never ship cookies, headers, bodies, or user IP to Sentry.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
    },

    // The SDK only filters the query before `#`; this app's routes and their
    // search params live in the hash. Scrub every URL we send (see scrubUrl).
    beforeSend(event) {
      if (event.request?.url) event.request.url = scrubUrl(event.request.url);
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      redactConsoleBreadcrumb(breadcrumb);
      const data = breadcrumb.data;
      // Navigation breadcrumbs fire before the URL changes, so replay is
      // paused before it can snapshot a sensitive URL.
      if (breadcrumb.category === 'navigation' && typeof data?.to === 'string') {
        pauseReplayOnSensitiveUrl(replay, data.to);
      }
      if (data) {
        for (const field of ['url', 'from', 'to'] as const) {
          if (typeof data[field] === 'string') data[field] = scrubUrl(data[field]);
        }
      }
      return breadcrumb;
    },
    beforeSendSpan(span) {
      // HTTP spans carry the request URL in their name ("GET https://…?…")
      // as well as in url.* attributes — including `url.query`, a bare query
      // string with no `?`, which scrubUrl would pass through unchanged.
      span.name = scrubUrlsInText(span.name);
      for (const [key, value] of Object.entries(span.attributes)) {
        if (typeof value !== 'string') continue;
        if (key.endsWith('query')) span.attributes[key] = scrubQueryString(value);
        else if (key.includes('url')) span.attributes[key] = scrubUrl(value);
      }
      return span;
    },

    integrations: [
      // Names transactions by route pattern (e.g. /Admin/Clients/:id) for
      // both the standalone hash router and the embedded memory router.
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      replay,
    ],

    tracesSampleRate: TRACES_SAMPLE_RATE[environment as SentryEnvironment] ?? 0.1,
    // tracePropagationTargets is left at the SDK default (same-origin only).
    // The deployed API hosts are cross-origin (spiglassapi.<domain>, …);
    // adding them here makes the browser send `sentry-trace` / `baggage`
    // headers, which every API's CORS policy must allow first or requests
    // will fail preflight.

    // Replay only sessions that hit an error — no routine session recording.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: REPLAY_ON_ERROR_SAMPLE_RATE[environment as SentryEnvironment] ?? 0.25,
  });

  // Transactions and the replay summary event (visited `urls`, `request.url`)
  // bypass beforeSend, but global event processors run on every event.
  Sentry.addEventProcessor((event) => {
    if (event.request?.url) event.request.url = scrubUrl(event.request.url);
    if (event.type === 'replay_event' || event.type === 'transaction') scrubUrlsDeep(event);
    return event;
  });

  // Replay starts buffering during init; stop it if the app opened on a
  // sensitive URL.
  pauseReplayOnSensitiveUrl(replay, window.location.href);
}

let replayPausedForUrl = false;
let replayStopping: Promise<void> = Promise.resolve();

const isSensitiveUrl = (url: string): boolean => scrubUrl(url) !== url;

/**
 * Each replay full snapshot records `window.location.href` verbatim (the
 * rrweb meta event — no SDK hook can filter it), so a hash query such as
 * `?Search=<owner name>` would reach Sentry. Stop replay, discarding its
 * buffer, while the URL holds a value scrubUrl would filter, and start
 * buffering again once it doesn't. Errors on those pages are still reported,
 * just without a replay. Exported for unit tests.
 *
 * Restarting is deferred: navigation breadcrumbs fire before pushState updates
 * `window.location`, and stop() is async. Restarting immediately would snapshot
 * the old (sensitive) URL and race the stop's teardown, so wait for both, then
 * recheck the URL actually in place.
 */
export function pauseReplayOnSensitiveUrl(
  replay: Pick<
    ReturnType<typeof Sentry.replayIntegration>,
    'getReplayId' | 'stop' | 'startBuffering'
  >,
  url: string
): void {
  if (isSensitiveUrl(url)) {
    if (replay.getReplayId() !== undefined) {
      replayPausedForUrl = true;
      replayStopping = replay.stop({ flush: false }).catch(() => undefined);
    }
    return;
  }
  if (!replayPausedForUrl) return;
  void replayStopping.then(() =>
    setTimeout(() => {
      if (replayPausedForUrl && !isSensitiveUrl(window.location.href)) {
        replayPausedForUrl = false;
        replay.startBuffering();
      }
    }, 0)
  );
}

export function captureException(error: Error, context?: Record<string, unknown>): void {
  if (!IS_ENABLED) {
    // In stub mode, log to sessionStorage for debugging
    // (matches existing ErrorBoundary behavior)
    try {
      const errorLog = {
        message: error.message,
        stack: error.stack,
        context,
        timestamp: new Date().toISOString(),
      };
      sessionStorage.setItem('last_error', JSON.stringify(errorLog));
    } catch {
      // Ignore storage errors
    }
    return;
  }

  // Custom context is not filtered by the SDK — scrub a `url` field (e.g. the
  // axios interceptor's failing request) the same way as other URLs.
  const details =
    context && typeof context.url === 'string'
      ? { ...context, url: scrubUrl(context.url) }
      : context;
  Sentry.captureException(error, details ? { contexts: { details } } : undefined);
}
